import { call, put, takeEvery, select } from 'redux-saga/effects';
import {
  defineKqlQuery,
  fetchSubmission,
  searchSubmissions,
  createSubmission,
  updateSubmission,
  submitSubmission,
  deleteSubmission,
  generateKey,
} from '@kineticdata/react';
import { C, cloneSubmission } from '@kineticdata/bundle-common';
import { actions, types } from '../modules/settingsPortfolio';
import { List } from 'immutable';

import { getDefaultContent } from '../../components/shared/content_manager/defaults';

/**
 * Feches all portfolios, retrieving all draft, pending, and active versions.
 * Only a small subset of fields are retrieved to prevent storing the full
 * Content JSON string in state for every record.
 */
export function* fetchPortfoliosSaga() {
  const { submissions, error } = yield call(searchSubmissions, {
    kapp: C.KAPP_SLUG_DATASTORE,
    form: C.FORM_SLUG_PORTFOLIO,
    limit: 1000,
    search: {
      q: defineKqlQuery()
        .in(`values[${C.FIELD_STATUS}]`, 'statuses')
        .end()({
        statuses: ['Draft', 'Pending', 'Active'],
      }),
      include: [
        'details',
        ...C.PORTFOLIO_PRIMARY_FIELDS.map(f => `values[${f}]`),
        'form',
      ],
    },
  });

  if (error) {
    yield put(actions.fetchPortfoliosFailure(error));
  } else {
    yield put(actions.fetchPortfoliosSuccess(submissions));
  }
}

/**
 * Fetches the selected portfolio, retrieving all draft, pending, and active
 * versions. The latest active version gets set as the current portfolio, and
 * the full list is used to update the portfolios state with the latest data for
 * this portfolio id.
 */
export function* fetchPortfolioSaga({ payload }) {
  const searcher = defineKqlQuery()
    .in(`values[${C.FIELD_STATUS}]`, 'statuses')
    .equals(`values[${C.FIELD_PORTFOLIO_ID}]`, 'id');

  const { submissions, error } = yield call(searchSubmissions, {
    kapp: C.KAPP_SLUG_DATASTORE,
    form: C.FORM_SLUG_PORTFOLIO,
    limit: 1000,
    search: {
      q: searcher.end()({
        statuses: ['Draft', 'Pending', 'Active'],
        id: payload,
      }),
      include: [
        'details',
        'values',
        'children',
        'children.values',
        'children.details',
        'form',
      ],
    },
  });

  if (error) {
    yield put(actions.fetchPortfolioFailure(error));
  } else {
    // Sort returned portfolios by most recently updated
    const sortedSubmissions = List(submissions)
      .sortBy(p => p.updatedAt)
      .reverse();
    // Get current portfolio as the most recently updated Active one, or just
    // the most recently updated one if there are no Active ones
    const currentPortfolio =
      sortedSubmissions.find(s => s.values[C.FIELD_STATUS] === 'Active') ||
      sortedSubmissions.first();

    if (!!currentPortfolio) {
      // Modify list of portfolios to only have the necessary values to prevent
      // the redux store from having a ton of data due to all the Content
      const list = sortedSubmissions.map(submission => ({
        ...submission,
        values: C.PORTFOLIO_PRIMARY_FIELDS.reduce(
          (vals, field) => ({ ...vals, [field]: submission.values[field] }),
          {},
        ),
      }));

      yield put(
        actions.fetchPortfolioSuccess({
          portfolioId: payload,
          record: currentPortfolio,
          list,
        }),
      );
    } else {
      yield put(
        actions.fetchPortfolioFailure({
          statusCode: 404,
          message: `Unable to locate the ${payload} portfolio`,
        }),
      );
    }
  }
}

/**
 * Fetches the selected portfolio revision.
 */
export function* fetchPortfolioRevisionSaga({
  payload: { revisionId, portfolioId },
}) {
  const { submission, error } = yield call(fetchSubmission, {
    id: revisionId,
    include: 'details,values,children,children.values,children.details,form',
  });

  if (error) {
    yield put(actions.fetchPortfolioRevisionFailure(error));
  } else if (
    !!portfolioId &&
    submission.values[C.FIELD_PORTFOLIO_ID] !== portfolioId
  ) {
    // If the Portfolio Id of the revision doesn't match the provided Portfolio
    // Id, the URL must have been manipulated.
    yield put(
      actions.fetchPortfolioRevisionFailure({
        message: `The selected revision doesn't belong to your current portfolio.`,
      }),
    );
  } else {
    // If no current portfolio set in state or the revision submission is for a
    // different portfolio, fetch the correct current portfolio
    const currentPortfolio = yield select(
      state => state.settingsPortfolio.current,
    );
    if (
      !currentPortfolio ||
      currentPortfolio.values[C.FIELD_PORTFOLIO_ID] !==
        submission.values[C.FIELD_PORTFOLIO_ID]
    ) {
      yield put(actions.fetchPortfolioRequest(portfolioId));
    }

    yield put(actions.fetchPortfolioRevisionSuccess(submission));
  }
}

/**
 * Creates a new portfolio in Draft status
 */
export function* createPortfolioSaga({ payload = {} }) {
  const { success, failure, complete, values = {} } = payload;

  // Generate a key to use as the Portfolio Id
  const portfolioId = generateKey(9);
  // TODO verify that key isn't already used, or maybe use submission id as key (set after cretiong)

  const { submission, error } = yield call(createSubmission, {
    kappSlug: C.KAPP_SLUG_DATASTORE,
    formSlug: C.FORM_SLUG_PORTFOLIO,
    values: {
      Name: `New Portfolio ${portfolioId}`,
      Type: 'Internal',
      ...values,
      [C.FIELD_PORTFOLIO_ID]: portfolioId,
      Status: 'Draft',
      Content: getDefaultContent(C.FORM_SLUG_PORTFOLIO),
    },
    include: 'details,values,form',
    completed: false,
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission) {
    if (typeof success === 'function') {
      success(submission);
    }

    // On success, fetch the portfolio so its submissions are included in the list
    yield put(actions.fetchPortfolioRequest(portfolioId));
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.portfolioActionComplete());
}

/**
 * Creates a portfolio revision by cloning an existing portfolio into Draft status
 */
export function* clonePortfolioSaga({ payload = {} }) {
  const { success, failure, complete, submissionId } = payload;

  const { submission, error } = yield call(cloneSubmission, {
    id: submissionId,
    values: { Status: 'Draft' },
    include: 'details,values,form',
    completed: false,
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission && typeof success === 'function') {
    success(submission);

    // On success, fetch the portfolio so its submissions are included in the list
    yield put(
      actions.fetchPortfolioRequest(submission.values[C.FIELD_PORTFOLIO_ID]),
    );
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.portfolioActionComplete());
}

/**
 * Updates a portfolio to an Expired status. If clone flag is true, first
 * creates a clone of the portfolio into draft status.
 */
export function* expirePortfolioSaga({ payload = {} }) {
  const {
    success,
    failure,
    complete,
    submissionId,
    clone = false,
    refetchItem = false,
  } = payload;

  // If clone flag is true, clone the submission into draft state
  if (clone) {
    const { error } = yield call(cloneSubmission, {
      id: submissionId,
      values: { Status: 'Draft' },
      completed: false,
    });

    if (error) {
      if (typeof failure === 'function') {
        failure(error);
      }
      return;
    }
  }

  const { submission, error } = yield call(updateSubmission, {
    id: submissionId,
    values: { Status: 'Expired', 'Locked By': '' },
    include: 'details,values,form',
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission) {
    if (typeof success === 'function') {
      success(submission);
    }

    // If the current portfolio still exists, refetch the portfolio
    if (refetchItem) {
      yield put(
        actions.fetchPortfolioRequest(submission.values[C.FIELD_PORTFOLIO_ID]),
      );
    }
    // Otherwise fetch portfolios list to get updated list of all data
    else {
      yield put(actions.fetchPortfoliosRequest());
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.portfolioActionComplete());
}

/**
 * Updates a portoflio revision, keeping the status as Draft
 */
export function* savePortfolioRevisionSaga({ payload = {} }) {
  const {
    success,
    failure,
    complete,
    submissionId,
    updatedAt,
    values,
  } = payload;

  // Retrieve the current submission to get its values and verify that the user
  // had the latest version and isn't overwriting anyones changes.
  const { submission: existingSubmission, error: existingError } = yield call(
    fetchSubmission,
    {
      id: submissionId,
      include: 'details,values,form',
    },
  );

  if (existingError) {
    failure(existingError);
  } else if (existingSubmission.updatedAt !== updatedAt) {
    failure({
      message:
        'This revision has been updated since you last loaded it. Please refresh your revision.',
    });
  } else {
    const profile = yield select(state => state.app.profile);

    const { submission, error } = yield call(updateSubmission, {
      id: submissionId,
      values: { ...values, Status: 'Draft', 'Locked By': profile.username },
      include: 'details,values,children,children.values,children.details,form',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (submission) {
      if (typeof success === 'function') {
        success(submission);
      }

      // If we updated the current revision, update the revision data
      const revision = yield select(state => state.settingsPortfolio.revision);
      if (revision && revision.id === submissionId) {
        yield put(actions.fetchPortfolioRevisionSuccess(submission));
      }
      // Otherwise fetch the updated portfolio so its submissions are included in the list
      else {
        yield put(
          actions.fetchPortfolioRequest(
            submission.values[C.FIELD_PORTFOLIO_ID],
          ),
        );
      }
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.portfolioActionComplete());
}

/**
 * Submits a portfolio revision, changing the status to Pending
 */
export function* submitPortfolioRevisionSaga({ payload = {} }) {
  const { success, failure, complete, submissionId, updatedAt } = payload;

  // Retrieve the current submission to get its values and verify that the user
  // is submitting the latest version.
  const { submission: existingSubmission, error: existingError } = yield call(
    fetchSubmission,
    {
      id: submissionId,
      include: 'details,values,form',
    },
  );

  if (existingError) {
    failure(existingError);
  } else if (existingSubmission.updatedAt !== updatedAt) {
    failure({
      message:
        'This revision has been updated since you last loaded it. Please refresh your revision, verify the data, and submit again.',
    });
  } else {
    const { submission, error } = yield call(submitSubmission, {
      id: submissionId,
      values: {
        ...existingSubmission.values,
        Status: 'Pending',
        'Locked By': '',
      },
      page: 'Page 1',
      include: 'details,values,children,children.values,children.details,form',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (submission) {
      if (typeof success === 'function') {
        success(submission);
      }

      // If we updated the current revision, refetch the revision data
      const revision = yield select(state => state.settingsPortfolio.revision);
      if (revision && revision.id === submissionId) {
        yield put(actions.fetchPortfolioRevisionSuccess(submission));
      }
      // Otherwise fetch the updated portfolio so its submissions are included in the list
      else {
        yield put(
          actions.fetchPortfolioRequest(
            submission.values[C.FIELD_PORTFOLIO_ID],
          ),
        );
      }
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.portfolioActionComplete());
}

/**
 * Locks or unlocks a portfolio revision
 */
export function* lockPortfolioRevisionSaga({ payload = {} }) {
  const { success, failure, complete, submissionId, unlock = false } = payload;

  const profile = yield select(state => state.app.profile);

  const { submission, error } = yield call(updateSubmission, {
    id: submissionId,
    values: { 'Locked By': unlock ? '' : profile.username },
    include: 'details,values,children,children.values,children.details,form',
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission) {
    if (typeof success === 'function') {
      success(submission);
    }

    // If we updated the current revision, update the revision data
    const revision = yield select(state => state.settingsPortfolio.revision);
    if (revision && revision.id === submissionId) {
      yield put(actions.fetchPortfolioRevisionSuccess(submission));
    }
    // Otherwise fetch the updated portfolio so its submissions are included in the list
    else {
      yield put(
        actions.fetchPortfolioRequest(submission.values[C.FIELD_PORTFOLIO_ID]),
      );
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.portfolioActionComplete());
}

/**
 * Deletes a portfolio revision
 */
export function* deletePortfolioRevisionSaga({ payload = {} }) {
  const {
    success,
    failure,
    complete,
    submissionId,
    cancel = false,
    refetchItem = false,
  } = payload;

  if (!cancel) {
    const { submission, error } = yield call(deleteSubmission, {
      id: submissionId,
      include: 'details,values,form',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    }
    if (!error) {
      if (typeof success === 'function') {
        success();
      }

      // If the current portfolio still exists, refetch the portfolio
      if (refetchItem) {
        yield put(
          actions.fetchPortfolioRequest(
            submission.values[C.FIELD_PORTFOLIO_ID],
          ),
        );
      }
      // Otherwise fetch portfolios list to get updated list of all data
      else {
        yield put(actions.fetchPortfoliosRequest());
      }
    }
  } else {
    // Retrieve the current submission to get its values so we can update it to
    // a Cancelled status
    const { submission: existingSubmission, error: existingError } = yield call(
      fetchSubmission,
      {
        id: submissionId,
        include: 'details,values,form',
      },
    );

    if (existingError) {
      failure(existingError);
    } else if (existingSubmission.values[C.FIELD_STATUS] !== 'Draft') {
      failure({
        message:
          'This revision status has been updated since you last loaded it. Please refresh your revision.',
      });
    } else {
      const { submission, error } = yield call(submitSubmission, {
        id: submissionId,
        values: {
          ...existingSubmission.values,
          Status: 'Cancelled',
          'Locked By': '',
        },
        page: 'Page 1',
        include: 'details,values,form',
      });

      if (error && typeof failure === 'function') {
        failure(error);
      }
      if (!error) {
        if (typeof success === 'function') {
          success();
        }

        // If the current portfolio still exists, refetch the portfolio
        if (refetchItem) {
          yield put(
            actions.fetchPortfolioRequest(
              submission.values[C.FIELD_PORTFOLIO_ID],
            ),
          );
        }
        // Otherwise fetch portfolios list to get updated list of all data
        else {
          yield put(actions.fetchPortfoliosRequest());
        }
      }
    }
  }

  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.portfolioActionComplete());
}

export function* watchSettingsPortfolio() {
  yield takeEvery(
    [types.FETCH_PORTFOLIOS_REQUEST, types.FETCH_PORTFOLIOS_BY_ID_REQUEST],
    fetchPortfoliosSaga,
  );
  yield takeEvery(types.FETCH_PORTFOLIO_REQUEST, fetchPortfolioSaga);
  yield takeEvery(
    types.FETCH_PORTFOLIO_REVISION_REQUEST,
    fetchPortfolioRevisionSaga,
  );
  yield takeEvery(types.CREATE_PORTFOLIO_REQUEST, createPortfolioSaga);
  yield takeEvery(types.CLONE_PORTFOLIO_REQUEST, clonePortfolioSaga);
  yield takeEvery(types.EXPIRE_PORTFOLIO_REQUEST, expirePortfolioSaga);
  yield takeEvery(
    types.SAVE_PORTFOLIO_REVISION_REQUEST,
    savePortfolioRevisionSaga,
  );
  yield takeEvery(
    types.LOCK_PORTFOLIO_REVISION_REQUEST,
    lockPortfolioRevisionSaga,
  );
  yield takeEvery(
    types.DELETE_PORTFOLIO_REVISION_REQUEST,
    deletePortfolioRevisionSaga,
  );
  yield takeEvery(
    types.SUBMIT_PORTFOLIO_REVISION_REQUEST,
    submitPortfolioRevisionSaga,
  );
}
